import React, { useState, useEffect } from 'react';
import { FiX, FiSearch } from 'react-icons/fi';
import type { Brand } from 'shared/types';
import type { AssignServiceToBrandData, Service } from '../../../shared/types/services';

interface AssignServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (data: AssignServiceToBrandData) => Promise<void>;
  brands: Brand[];
  availableServices: Service[];
  selectedBrand?: Brand | null;
  isLoading?: boolean;
  onBrandChange?: (brandId: string) => void;
}

const AssignServiceModal: React.FC<AssignServiceModalProps> = ({
  isOpen,
  onClose,
  onAssign,
  brands,
  availableServices,
  selectedBrand,
  isLoading = false,
  onBrandChange,
}) => {
  const [formData, setFormData] = useState<AssignServiceToBrandData>({
    brandId: '',
    serviceId: '',
    isActive: true,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (selectedBrand) {
      setFormData(prev => ({ ...prev, brandId: selectedBrand.id }));
    } else {
      setFormData({
        brandId: '',
        serviceId: '',
        isActive: true,
      });
    }
    setSearchTerm('');
    setErrors({});
  }, [selectedBrand, isOpen]);

  const filteredServices = availableServices.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.brandId) {
      newErrors.brandId = 'Please select a brand';
    }

    if (!formData.serviceId) {
      newErrors.serviceId = 'Please select a service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    try {
      await onAssign(formData);
      onClose();
    } catch (error) {
      console.error('Error assigning service to brand:', error);
    }
  };

  const handleBrandChange = (brandId: string) => {
    setFormData(prev => ({ ...prev, brandId, serviceId: '' }));
    if (onBrandChange) {
      onBrandChange(brandId);
    }
    if (errors.brandId) {
      setErrors(prev => ({ ...prev, brandId: '' }));
    }
  };

  const handleServiceChange = (serviceId: string) => {
    setFormData(prev => ({ ...prev, serviceId }));
    if (errors.serviceId) {
      setErrors(prev => ({ ...prev, serviceId: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Assign Service to Brand
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            disabled={isLoading}
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="brand" className="block text-sm font-medium text-gray-700">
              Brand *
            </label>
            <select
              id="brand"
              value={formData.brandId}
              onChange={(e) => handleBrandChange(e.target.value)}
              className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                errors.brandId ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={isLoading || !!selectedBrand}
            >
              <option value="">Select a brand</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name} {!brand.isActive && '(Inactive)'}
                </option>
              ))}
            </select>
            {errors.brandId && (
              <p className="mt-1 text-sm text-red-600">{errors.brandId}</p>
            )}
          </div>

          {formData.brandId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Services *
              </label>
              
              {/* Search */}
              <div className="relative mb-4">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoading}
                />
              </div>

              {/* Services List */}
              <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto">
                {filteredServices.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {searchTerm ? 'No services found matching your search' : 'No available services for this brand'}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredServices.map((service) => (
                      <label
                        key={service.id}
                        className={`flex items-start p-4 cursor-pointer hover:bg-gray-50 ${
                          formData.serviceId === service.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="service"
                          value={service.id}
                          checked={formData.serviceId === service.id}
                          onChange={(e) => handleServiceChange(e.target.value)}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          disabled={isLoading}
                        />
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {service.name}
                            </span>
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              {service.code}
                            </span>
                            {service.category && (
                              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                {service.category}
                              </span>
                            )}
                          </div>
                          {service.description && (
                            <p className="mt-1 text-sm text-gray-500">
                              {service.description}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {errors.serviceId && (
                <p className="mt-1 text-sm text-red-600">{errors.serviceId}</p>
              )}
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              disabled={isLoading}
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.brandId || !formData.serviceId}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Assigning...' : 'Assign Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignServiceModal;